<template>
  <div class="AiExpand" :class="{ 'AiExpand--reverse': reverse }">
    <slot name="activator" @click="activate">
      <ai-button
        v-show="!expanded"
        :label="labelOpen"
        :aria-controls="controlsId"
        :aria-expanded="false"
        :aria-disabled="expanded"
        variant="tertiary"
        @click="activate">
        <template #right>
          <ai-icon :size="24" name="chevron-down" class="AiExpand-icon" />
        </template>
      </ai-button>

      <ai-button
        v-show="expanded"
        :label="labelHide"
        :aria-controls="controlsId"
        :aria-expanded="true"
        :aria-disabled="!expanded"
        variant="tertiary"
        @click="activate">
        <template #right>
          <ai-icon
            :size="24"
            name="chevron-down"
            class="AiExpand-icon AiExpand-icon--expanded" />
        </template>
      </ai-button>
    </slot>

    <div
      :id="controlsId"
      class="AiExpand-body"
      :class="{ 'AiExpand-body--expanded': expanded }">
      <slot />
    </div>
    <ai-separator v-if="expanded" :margin="0" />
  </div>
</template>

<script setup lang="ts">
type Props = {
  controlsId?: string;
  modelValue?: boolean;
  labelOpen?: string;
  labelHide?: string;
  reverse?: boolean;
};

type Emits = {
  (event: 'update:modelValue', value: boolean): void;
};

const props = withDefaults(defineProps<Props>(), {
  controlsId: '',
  labelOpen: undefined,
  labelHide: undefined,
  modelValue: undefined,
  reverse: false,
});
const emits = defineEmits<Emits>();

const internalValue = ref(false);

const expanded = computed({
  get() {
    if (typeof props.modelValue === 'boolean') return props.modelValue;

    return internalValue.value;
  },
  set(value) {
    if (typeof props.modelValue === 'boolean') {
      return emits('update:modelValue', value);
    }

    internalValue.value = value;
  },
});

const activate = () => {
  expanded.value = !expanded.value;
};
</script>

<style scoped lang="scss">
.AiExpand {
  &--reverse {
    display: flex;
    flex-direction: column-reverse;
  }
}
.AiExpand-icon {
  display: inline-block;
  transition: transform 200ms;

  &--expanded {
    transform: rotate(180deg);
  }
}

.AiExpand-body {
  height: 0;
  overflow: hidden;

  &--expanded {
    height: fit-content;
    width: 100%;
  }
}
</style>
